export interface AutoLoginDeps {
    /** Current session state — skip the whole chain if already logged in (mirror warm). */
    isLoggedIn: boolean
    /** One-Tap (the only step with UI) runs on the homepage only. */
    isHomepage: boolean
    /** Restore + refresh an expired cognito mirror. Silent (no UI). Resolves true if a session is now active. */
    restoreSession: () => Promise<boolean>
    /** Silent Facebook restore (`getLoginStatus`). Resolves true if a session is now active. */
    facebookRestore: () => Promise<boolean>
    /** Prompt Google One-Tap and establish a session. Resolves true if logged in. */
    oneTapRestore: () => Promise<boolean>
}

/** Run a chain step, swallowing any rejection — a failed provider must never break the page. */
async function step(attempt: () => Promise<boolean>): Promise<boolean> {
    try {
        return await attempt()
    } catch {
        return false
    }
}

/**
 * Silent auto-login chain decision logic (ui-react `performAutoLogin` parity, minus the
 * Google Button step — OOS-8). Runs only when logged-out, in order: mirror restore/refresh
 * → Facebook silent restore → Google One-Tap, stopping at the first that establishes a
 * session. The first two are silent and run on every route; One-Tap shows UI, so it is
 * gated to the homepage (FR-8 / AC-13). Never throws — each step is fail-safe.
 */
export async function runAutoLoginChain(deps: AutoLoginDeps): Promise<void> {
    if (deps.isLoggedIn) {
        return
    }
    if (await step(deps.restoreSession)) {
        return
    }
    if (await step(deps.facebookRestore)) {
        return
    }
    if (deps.isHomepage) {
        await step(deps.oneTapRestore)
    }
}
